import type { AssetMetricCatalogEntry } from '@src/providers/CoinMetricsApiProvider';
import {
  useCoinMetricsApiKey,
  useGetAssetMetricsCatalogQuery,
  useGetAssetsReferenceQuery,
} from '@src/providers/CoinMetricsApiProvider';
import { useCurrenciesContext } from '@talos/kyoko';
import { useMemo } from 'react';
import { CM_MAX_PAGE_SIZE } from './tokens';

interface UseSignalsAssetMapOptions {
  pollingInterval?: number;
  isAssetEligible?: (entry: AssetMetricCatalogEntry) => boolean;
}

interface UseSignalsAssetMapResult {
  /** Talos `Currency.Symbol` values available for all metrics, sorted alphabetically. */
  talosSymbols: string[];
  /** Lookup: Talos symbol -> CoinMetrics asset id (e.g. `'BTC' -> 'btc'`). */
  cmAssetIdByTalosSymbol: ReadonlyMap<string, string>;
  /** Reverse lookup: CoinMetrics asset id -> Talos symbol (e.g. `'btc' -> 'BTC'`). */
  talosSymbolByCmAssetId: ReadonlyMap<string, string>;
  /**
   * The catalog response backing this result, unfiltered by `isAssetEligible` even when that option was
   * supplied — for callers that need a second, differently-scoped eligibility pass over the same cached
   * response instead of issuing a second query.
   */
  catalogEntries: readonly AssetMetricCatalogEntry[];
  isLoading: boolean;
  isError: boolean;
}

const EMPTY_MAP: ReadonlyMap<string, string> = new Map();
const EMPTY_SYMBOLS: string[] = [];
const EMPTY_CATALOG_ENTRIES: readonly AssetMetricCatalogEntry[] = [];
const acceptAllAssets = () => true;

/** Resolves CoinMetrics metrics to the Talos currency symbols available in the current org. */
export function useSignalsAssetMap(metrics: string[], options?: UseSignalsAssetMapOptions): UseSignalsAssetMapResult {
  const apiKey = useCoinMetricsApiKey();
  const { currenciesBySymbol } = useCurrenciesContext();
  const isAssetEligible = options?.isAssetEligible ?? acceptAllAssets;

  // Sort before joining so ['A','B'] and ['B','A'] hit the same RTK Query cache entry.
  const metricsParam = useMemo(() => [...metrics].toSorted().join(','), [metrics]);

  const catalogResult = useGetAssetMetricsCatalogQuery(
    { metrics: metricsParam, page_size: CM_MAX_PAGE_SIZE },
    { skip: !apiKey, pollingInterval: options?.pollingInterval }
  );

  const catalogEntries = catalogResult.data?.data ?? EMPTY_CATALOG_ENTRIES;

  const cmAssetIds = useMemo(
    () => catalogEntries.filter(isAssetEligible).map(entry => entry.asset),
    [catalogEntries, isAssetEligible]
  );

  // Fetch ALL Talos-mapped assets without an assets filter, so the URL stays compact
  // regardless of how many assets support the metric. Both queries run in parallel;
  // the intersection with cmAssetIds happens client-side in the memo below.
  const referenceDataResult = useGetAssetsReferenceQuery(
    { include: 'talos', page_size: CM_MAX_PAGE_SIZE },
    { skip: !apiKey }
  );

  return useMemo(() => {
    // isUninitialized covers the skip: !apiKey state — key not yet synced into Redux.
    const isLoading =
      catalogResult.isLoading ||
      catalogResult.isUninitialized ||
      (cmAssetIds.length > 0 && !referenceDataResult.data && !referenceDataResult.isError);
    const isError = catalogResult.isError || referenceDataResult.isError;
    const referenceData = referenceDataResult.data?.data;

    if (!referenceData) {
      return {
        talosSymbols: EMPTY_SYMBOLS,
        cmAssetIdByTalosSymbol: EMPTY_MAP,
        talosSymbolByCmAssetId: EMPTY_MAP,
        catalogEntries,
        isLoading,
        isError,
      };
    }

    const cmAssetIdSet = new Set(cmAssetIds);
    const cmAssetIdByTalosSymbol = new Map<string, string>();
    const talosSymbolByCmAssetId = new Map<string, string>();

    for (const entry of referenceData) {
      if (!cmAssetIdSet.has(entry.asset)) {
        continue;
      }
      const talosSymbol = entry.talos?.at(0)?.symbol;
      if (!talosSymbol) {
        continue;
      }
      // Drop any symbol the user's Talos instance doesn't know about.
      if (!currenciesBySymbol.has(talosSymbol)) {
        continue;
      }
      // First write wins if CM ever returns duplicates for the same Talos symbol.
      if (!cmAssetIdByTalosSymbol.has(talosSymbol)) {
        cmAssetIdByTalosSymbol.set(talosSymbol, entry.asset);
        talosSymbolByCmAssetId.set(entry.asset, talosSymbol);
      }
    }

    return {
      talosSymbols: Array.from(cmAssetIdByTalosSymbol.keys()).toSorted(),
      cmAssetIdByTalosSymbol,
      talosSymbolByCmAssetId,
      catalogEntries,
      isLoading,
      isError,
    };
  }, [
    catalogResult.isLoading,
    catalogResult.isUninitialized,
    catalogResult.isError,
    cmAssetIds,
    catalogEntries,
    referenceDataResult.data,
    referenceDataResult.isError,
    currenciesBySymbol,
  ]);
}
